import type { TFilters } from 'src/providers/FiltersProvider';
import type { Source, CustomerType } from 'src/pages/reports/common/consts';

import dayjs from 'dayjs';
import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { ROLES } from 'src/lib/consts';

import { useAuthContext } from 'src/auth/hooks';

export const mockDefaultFilters: TFilters = {
  locationIds: [],
  dateFrom: dayjs(new Date()).startOf('year').format('YYYY-MM-DD'),
  dateTo: dayjs(new Date()).endOf('day').format('YYYY-MM-DD'),
  source: 'combined',
  customerType: 'all',
  eventId: '',
  threshold: 1,
  repeatType: 'monthly',
};

type TUseFilters = {
  filters: TFilters;
  resetFilters: () => void;
  updateFilters: (newFilters: Partial<TFilters>) => void;
};

export function useFilters({
  defaultFilters = mockDefaultFilters,
  enforceLocationAccess = true, // New parameter
}: {
  defaultFilters?: TFilters;
  enforceLocationAccess?: boolean;
}): TUseFilters {
  const { user } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const locationIds = searchParams.get('locationIds')?.split(',') || defaultFilters.locationIds;
    const dateFrom = searchParams.get('dateFrom') || defaultFilters.dateFrom;
    const dateTo = searchParams.get('dateTo') || defaultFilters.dateTo;
    const source = (searchParams.get('source') as Source) || defaultFilters.source;
    const customerType =
      (searchParams.get('customerType') as CustomerType) || defaultFilters.customerType;
    const eventId = searchParams.get('eventId') || defaultFilters.eventId;
    const threshold = searchParams.get('threshold') || defaultFilters.threshold;
    const repeatType = searchParams.get('repeatType') || defaultFilters.repeatType;

    // Apply location restrictions if enabled
    let filteredLocationIds = locationIds;

    if (enforceLocationAccess && user?.role === ROLES.MANAGER) {
      if (!locationIds.length) {
        filteredLocationIds = user?.approved_locations || [];
      } else {
        filteredLocationIds = filteredLocationIds = locationIds.filter((locationId) =>
          user?.approved_locations?.includes(locationId)
        );
      }
    }

    return {
      locationIds: filteredLocationIds,
      dateFrom,
      dateTo,
      source,
      customerType,
      eventId,
      threshold: threshold ? parseInt(`${threshold}`, 10) : defaultFilters.threshold,
      repeatType,
    };
  }, [searchParams, defaultFilters, enforceLocationAccess, user]);

  const resetFilters = useCallback(() => {
    setSearchParams({});
  }, [setSearchParams]);

  const updateFilters = useCallback<TUseFilters['updateFilters']>(
    (newFilters) => {
      setSearchParams((currentSearchParams) => {
        const {
          locationIds,
          dateFrom,
          dateTo,
          source,
          customerType,
          eventId,
          repeatType,
          threshold,
        } = {
          ...filters,
          ...newFilters,
        };

        if (customerType) {
          currentSearchParams.set('customerType', customerType);
        }

        if (locationIds.length > 0) {
          currentSearchParams.set('locationIds', locationIds.join(','));
        } else {
          currentSearchParams.delete('locationIds');
        }

        if (dateFrom) {
          currentSearchParams.set('dateFrom', dateFrom);
        } else {
          currentSearchParams.delete('dateFrom');
        }

        if (dateTo) {
          currentSearchParams.set('dateTo', dateTo);
        } else {
          currentSearchParams.delete('dateTo');
        }

        if (source) {
          currentSearchParams.set('source', source);
        } else {
          currentSearchParams.delete('source');
        }

        if (eventId) {
          currentSearchParams.set('eventId', eventId);
        } else {
          currentSearchParams.delete('eventId');
        }

        if (threshold) {
          currentSearchParams.set('threshold', threshold.toString());
        } else {
          currentSearchParams.delete('threshold');
        }

        if (repeatType) {
          currentSearchParams.set('repeatType', repeatType);
        } else {
          currentSearchParams.delete('repeatType');
        }

        return currentSearchParams;
      });
    },
    [filters, setSearchParams]
  );

  return {
    filters,
    resetFilters,
    updateFilters,
  };
}
